/**
 * Agent API Client — Kommunikation mit dem Agent-Orchestrator
 *
 * chat()       — Klassischer Request/Response
 * chatStream() — SSE Streaming mit Fortschritts-Events
 */

import client from './client';

/**
 * SSE-Stream parsen und Events an Callback weiterleiten
 * @param {Response} response — fetch Response mit text/event-stream
 * @param {function} onEvent — Callback: (event) => void
 * @returns {object} — Finales Ergebnis (type: 'result')
 */
async function parseSSEStream(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // SSE Lines parsen: "data: {...}\n\n"
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Unvollstaendige letzte Zeile behalten

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'result') {
            finalResult = event;
          }
          onEvent(event);
        } catch (e) {
          // Unparseable line — ignorieren (z.B. keepalive)
        }
      }
      // ": keepalive" Zeilen werden automatisch ignoriert
    }
  }

  return finalResult;
}

const agentApi = {
  /**
   * Chat mit dem Agent (klassisch, ohne Streaming)
   * @param {string} message — User-Nachricht
   * @param {object} options — { sessionId, context: { module, pageId, postId } }
   */
  chat: (message, options = {}) => {
    const body = { message };
    if (options.sessionId) body.sessionId = options.sessionId;
    if (options.context && Object.keys(options.context).length > 0) body.context = options.context;
    return client.post('/agent/chat', body);
  },

  /**
   * Chat mit SSE Streaming — Fortschritt in Echtzeit
   * @param {string} message
   * @param {object} options — { sessionId, context }
   * @param {function} onEvent — Callback fuer Progress-Events
   * @returns {object} — Finales Ergebnis
   */
  chatStream: async (message, options = {}, onEvent = () => {}) => {
    const body = { message };
    if (options.sessionId) body.sessionId = options.sessionId;
    if (options.context && Object.keys(options.context).length > 0) body.context = options.context;

    // Auth-Token aus localStorage (gleicher Key wie client.js Interceptor)
    const token = localStorage.getItem('accessToken');
    const baseURL = client.defaults?.baseURL || '/api/v1';

    const response = await fetch(`${baseURL}/agent/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const err = await response.text().catch(() => 'Stream-Fehler');
      throw new Error(`HTTP ${response.status}: ${err}`);
    }

    return parseSSEStream(response, onEvent);
  },

  /**
   * Approve-All mit SSE Streaming
   * @param {string} sessionId
   * @param {object} context
   * @param {function} onEvent
   * @returns {object} — Finales Ergebnis
   */
  approveAllStream: async (sessionId, context = {}, onEvent = () => {}) => {
    const body = { sessionId, context };
    const token = localStorage.getItem('accessToken');
    const baseURL = client.defaults?.baseURL || '/api/v1';

    const response = await fetch(`${baseURL}/agent/approve-all/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const err = await response.text().catch(() => 'Stream-Fehler');
      throw new Error(`HTTP ${response.status}: ${err}`);
    }

    return parseSSEStream(response, onEvent);
  },

  /**
   * Schreib-Aktion freigeben oder ablehnen
   * @param {string} approvalId
   * @param {boolean} approved
   */
  approve: (approvalId, approved = true) =>
    client.post('/agent/approve', { approvalId, approved }),

  /**
   * Alle Pending Approvals auf einmal freigeben + Auto-Continue (klassisch)
   * @param {string} sessionId
   * @param {object} context — Modul-Kontext
   */
  approveAll: (sessionId, context = {}) =>
    client.post('/agent/approve-all', { sessionId, context }, { timeout: 120000 }),

  /**
   * Verfuegbare Tools auflisten
   */
  getTools: () =>
    client.get('/agent/tools')
};

export default agentApi;
